// @ts-nocheck
// ============================================
// Navigation State Persistence - утилита для сохранения и восстановления состояния навигации
// ============================================

// Ключи для localStorage
export const NAVIGATION_STORAGE_KEYS = {
  APP_ID: 'currentAppId',
  VERSION_ID: 'currentVersionId',
  SCREEN_ID: 'currentScreenId',
} as const;

export type NavigationState = {
  currentAppId: string | null;
  currentVersionId: string | null;
  currentScreenId: string | null;
};

/**
 * Сохранить текущее состояние навигации в localStorage
 */
export function saveNavigationState(state: NavigationState): void {
  if (typeof window === 'undefined') return;
  
  try {
    if (state.currentAppId) {
      localStorage.setItem(NAVIGATION_STORAGE_KEYS.APP_ID, state.currentAppId);
    } else {
      localStorage.removeItem(NAVIGATION_STORAGE_KEYS.APP_ID);
    }
    
    if (state.currentVersionId) {
      localStorage.setItem(NAVIGATION_STORAGE_KEYS.VERSION_ID, state.currentVersionId);
    } else {
      localStorage.removeItem(NAVIGATION_STORAGE_KEYS.VERSION_ID);
    }
    
    if (state.currentScreenId) {
      localStorage.setItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID, state.currentScreenId);
    } else {
      localStorage.removeItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID);
    }
    
    console.log('[NavigationState] Saved to localStorage:', state);
  } catch (error) {
    console.error('[NavigationState] Error saving to localStorage:', error);
  }
}

/**
 * Сохранить только ID приложения
 */
export function saveAppId(appId: string | null): void {
  if (typeof window === 'undefined') return;
  
  if (appId) {
    localStorage.setItem(NAVIGATION_STORAGE_KEYS.APP_ID, appId);
  } else {
    localStorage.removeItem(NAVIGATION_STORAGE_KEYS.APP_ID);
  }
}

/**
 * Сохранить только ID версии
 */
export function saveVersionId(versionId: string | null): void {
  if (typeof window === 'undefined') return;
  
  if (versionId) {
    localStorage.setItem(NAVIGATION_STORAGE_KEYS.VERSION_ID, versionId);
  } else {
    localStorage.removeItem(NAVIGATION_STORAGE_KEYS.VERSION_ID);
  }
}

/**
 * Сохранить только ID экрана
 */
export function saveScreenId(screenId: string | null): void {
  if (typeof window === 'undefined') return;
  
  if (screenId) {
    localStorage.setItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID, screenId);
  } else {
    localStorage.removeItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID);
  }
}

/**
 * Получить сохранённое состояние навигации из localStorage
 */
export function getNavigationState(): NavigationState {
  if (typeof window === 'undefined') {
    return {
      currentAppId: null,
      currentVersionId: null,
      currentScreenId: null,
    };
  }
  
  return {
    currentAppId: localStorage.getItem(NAVIGATION_STORAGE_KEYS.APP_ID),
    currentVersionId: localStorage.getItem(NAVIGATION_STORAGE_KEYS.VERSION_ID),
    currentScreenId: localStorage.getItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID),
  };
}

/**
 * Проверить, есть ли сохранённое состояние навигации
 */
export function hasNavigationState(): boolean {
  if (typeof window === 'undefined') return false;
  
  return !!(
    localStorage.getItem(NAVIGATION_STORAGE_KEYS.APP_ID) ||
    localStorage.getItem(NAVIGATION_STORAGE_KEYS.VERSION_ID) ||
    localStorage.getItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID)
  );
}

/**
 * Очистить сохранённое состояние навигации
 */
export function clearNavigationState(): void {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem(NAVIGATION_STORAGE_KEYS.APP_ID);
  localStorage.removeItem(NAVIGATION_STORAGE_KEYS.VERSION_ID);
  localStorage.removeItem(NAVIGATION_STORAGE_KEYS.SCREEN_ID);
  
  console.log('[NavigationState] Cleared from localStorage');
}

/**
 * Проверить, существует ли сохранённый app в списке приложений
 */
export function validateSavedAppExists(
  savedAppId: string | null,
  apps: Array<{ id: string }>
): { isValid: boolean; appExists: boolean } {
  if (!savedAppId) {
    return { isValid: false, appExists: false };
  }
  
  const appExists = apps.some(app => app.id === savedAppId);
  return { isValid: appExists, appExists };
}

/**
 * Найти валидную версию в приложении
 */
export function findValidVersion(
  app: { versions?: Array<{ id: string; isActive?: boolean; screens?: Array<{ id: string }> }> },
  savedVersionId: string | null
): { version: typeof app.versions[0] | null; screenId: string | null } {
  const versions = app.versions || [];
  
  if (!versions.length) {
    return { version: null, screenId: null };
  }
  
  // Ищем сохранённую версию
  let version = savedVersionId 
    ? versions.find(v => v.id === savedVersionId)
    : null;
  
  // Если не нашли сохранённую, ищем активную или первую
  if (!version) {
    version = versions.find(v => v.isActive) || versions[0];
  }
  
  // Находим первый экран версии
  const screenId = version?.screens?.[0]?.id || null;
  
  return { version: version || null, screenId };
}
